import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import BackToAdminLink from "@/components/BackToAdminLink";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Question,
  ArrowClockwise,
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  X,
  ArrowUp,
  ArrowDown,
  Folder,
} from "@phosphor-icons/react";

// Iter34.5 / Iter36 — Edit FAQ admin page.
// DB-backed FAQ items shown on /help, grouped by admin-managed categories.
export default function AdminFaqsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, question, answer, category_id} or {isNew: true, ...}
  const [busy, setBusy] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [renamingCatId, setRenamingCatId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [faqsRes, catsRes] = await Promise.all([
        axios.get(`${API}/help/faqs`),
        axios.get(`${API}/admin/faq-categories`),
      ]);
      setItems(faqsRes.data.faqs || []);
      setCategories(catsRes.data.categories || []);
    } catch (e) {
      toast.error("Could not load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const catNameById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c.name;
    return m;
  }, [categories]);

  const startNew = () =>
    setEditing({ isNew: true, question: "", answer: "", category_id: categories[0]?.id || null });
  const startEdit = (item) =>
    setEditing({
      id: item.id,
      question: item.question,
      answer: item.answer,
      category_id: item.category_id || null,
    });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if ((editing.question || "").trim().length < 5) {
      toast.error("Question must be at least 5 characters");
      return;
    }
    if ((editing.answer || "").trim().length < 10) {
      toast.error("Answer must be at least 10 characters");
      return;
    }
    setBusy(true);
    try {
      if (editing.isNew) {
        await axios.post(`${API}/admin/faqs`, {
          question: editing.question.trim(),
          answer: editing.answer.trim(),
          category_id: editing.category_id || null,
        });
        toast.success("FAQ added");
      } else {
        await axios.patch(`${API}/admin/faqs/${editing.id}`, {
          question: editing.question.trim(),
          answer: editing.answer.trim(),
          category_id: editing.category_id || null,
        });
        toast.success("FAQ updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save FAQ");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.question}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/faqs/${item.id}`);
      toast.success("FAQ deleted");
      load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const move = async (index, direction) => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= items.length) return;
    const next = [...items];
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setItems(next);
    try {
      await axios.post(`${API}/admin/faqs/reorder`, {
        ids_in_order: next.map((i) => i.id),
      });
    } catch (e) {
      toast.error("Reorder failed — reloading");
      load();
    }
  };

  const addCategory = async () => {
    const name = newCatName.trim();
    if (name.length < 2) {
      toast.error("Category name must be at least 2 characters");
      return;
    }
    try {
      await axios.post(`${API}/admin/faq-categories`, { name });
      toast.success("Category added");
      setNewCatName("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add category");
    }
  };

  const renameCategory = async (cat) => {
    const name = (renameValue || "").trim();
    if (name.length < 2 || name === cat.name) {
      setRenamingCatId(null);
      setRenameValue("");
      return;
    }
    try {
      await axios.patch(`${API}/admin/faq-categories/${cat.id}`, { name });
      toast.success("Category renamed");
      setRenamingCatId(null);
      setRenameValue("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename category");
    }
  };

  const deleteCategory = async (cat) => {
    if (
      !window.confirm(
        `Delete category "${cat.name}"? FAQs currently assigned to it will become uncategorised (they won't be deleted).`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/faq-categories/${cat.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete category");
    }
  };

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-faqs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <BackToAdminLink />
          <div className="section-eyebrow flex items-center gap-2">
            <Question size={16} weight="bold" /> Admin · Help
          </div>
          <h1 className="font-heading uppercase text-4xl mt-2">Edit FAQs</h1>
          <p className="text-black/75 mt-3 leading-relaxed">
            The FAQ list shown to every user on the /help page, grouped by category. Manage
            categories below (add, rename, delete), then assign each FAQ to one when you write it.
            Reorder items with the ↑ / ↓ arrows. Changes go live instantly — there's no publish
            step. Aim for short questions (users skim these) and warm, plain-English answers that
            sound like <em>you</em>, not a corporate help doc.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-primary"
            onClick={startNew}
            data-testid="admin-faq-add-btn"
          >
            <Plus size={14} weight="bold" /> Add FAQ
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={load}
            disabled={loading}
            data-testid="admin-faq-reload"
          >
            <ArrowClockwise size={14} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Categories manager */}
      <div className="card" data-testid="admin-faq-categories">
        <h2 className="font-heading uppercase text-xl flex items-center gap-2">
          <Folder size={20} weight="duotone" /> Categories
        </h2>
        <p className="text-xs text-black/60 mt-1">
          Categories group your FAQs on the public /help page. Deleting a category leaves its FAQs
          intact — they just become uncategorised.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {categories.length === 0 && (
            <p className="text-sm text-black/50 italic">No categories yet.</p>
          )}
          {categories.map((c) => {
            const isRenaming = renamingCatId === c.id;
            return (
              <div
                key={c.id}
                className="flex items-center gap-2 border-[1.5px] border-black/25 rounded-lg px-3 py-1.5 text-sm bg-[#FFF7E5]"
                data-testid={`admin-faq-category-${c.id}`}
              >
                {isRenaming ? (
                  <>
                    <input
                      type="text"
                      className="input !py-1 !px-2 !text-sm w-40"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      maxLength={100}
                      autoFocus
                      data-testid={`admin-faq-category-rename-input-${c.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => renameCategory(c)}
                      className="text-xs font-semibold underline"
                      data-testid={`admin-faq-category-rename-save-${c.id}`}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRenamingCatId(null);
                        setRenameValue("");
                      }}
                      className="text-xs text-black/60 hover:text-black"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="font-semibold">{c.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setRenamingCatId(c.id);
                        setRenameValue(c.name);
                      }}
                      className="text-black/60 hover:text-black"
                      title="Rename"
                      data-testid={`admin-faq-category-rename-${c.id}`}
                    >
                      <PencilSimple size={12} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteCategory(c)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                      data-testid={`admin-faq-category-delete-${c.id}`}
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <input
            type="text"
            className="input !py-2 max-w-xs"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            placeholder="New category name…"
            maxLength={100}
            data-testid="admin-faq-category-new-input"
          />
          <button
            type="button"
            onClick={addCategory}
            className="btn-secondary"
            data-testid="admin-faq-category-new-btn"
          >
            <Plus size={14} weight="bold" /> Add category
          </button>
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className="card border-2 border-black" data-testid="admin-faq-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-heading uppercase text-xl">
              {editing.isNew ? "New FAQ" : "Edit FAQ"}
            </h2>
            <button
              type="button"
              onClick={cancel}
              className="text-black/60 hover:text-black"
              data-testid="admin-faq-editor-close"
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Question</label>
              <input
                type="text"
                className="input"
                value={editing.question}
                onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                placeholder="e.g. How do I add a new offer after building my forecast?"
                autoFocus
                maxLength={280}
                data-testid="admin-faq-question"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Category</label>
              <select
                className="input"
                value={editing.category_id || ""}
                onChange={(e) =>
                  setEditing({ ...editing, category_id: e.target.value || null })
                }
                data-testid="admin-faq-category-select"
              >
                <option value="">— Uncategorised —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Answer</label>
              <RichTextEditor
                value={editing.answer}
                onChange={(html) => setEditing({ ...editing, answer: html })}
                placeholder="Warm, plain-English answer."
                features={{
                  headings: false,
                  align: false,
                  media: false,
                  tables: false,
                  embeds: false,
                  highlight: false,
                }}
                testId="admin-faq-answer"
                minHeight={200}
              />
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
              data-testid="admin-faq-save"
            >
              <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save FAQ"}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={cancel}
              data-testid="admin-faq-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3" data-testid="admin-faq-list">
        {loading && items.length === 0 ? (
          <p className="text-center text-black/55 py-8">Loading…</p>
        ) : items.length === 0 ? (
          <div className="card text-center">
            <p className="text-black/65">No FAQs yet — click "Add FAQ" to create the first one.</p>
          </div>
        ) : (
          items.map((item, i) => (
            <div
              key={item.id}
              className="card !p-4 flex items-start gap-3"
              data-testid={`admin-faq-row-${item.id}`}
            >
              {/* Reorder arrows */}
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => move(i, "up")}
                  disabled={i === 0}
                  className="!px-1.5 !py-1 rounded border-[1.5px] border-black/25 hover:bg-[#FFF7E5] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                  data-testid={`admin-faq-up-${item.id}`}
                >
                  <ArrowUp size={12} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, "down")}
                  disabled={i === items.length - 1}
                  className="!px-1.5 !py-1 rounded border-[1.5px] border-black/25 hover:bg-[#FFF7E5] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                  data-testid={`admin-faq-down-${item.id}`}
                >
                  <ArrowDown size={12} weight="bold" />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-sm">{item.question}</h3>
                  <span className="text-[10px] uppercase tracking-widest border border-black/30 rounded px-2 py-0.5 bg-[#FFF7E5]">
                    {catNameById[item.category_id] || "Uncategorised"}
                  </span>
                </div>
                <p className="text-xs text-black/65 mt-1 whitespace-pre-wrap leading-relaxed">
                  {item.answer}
                </p>
                <div className="text-[10px] text-black/40 mt-2">
                  Position {i + 1} · Updated{" "}
                  {item.updated_at
                    ? new Date(item.updated_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "—"}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => startEdit(item)}
                  className="btn-secondary !px-2 !py-1 !text-xs"
                  data-testid={`admin-faq-edit-${item.id}`}
                >
                  <PencilSimple size={12} weight="bold" /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => remove(item)}
                  className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-red-600 text-red-700 hover:bg-red-50 font-semibold inline-flex items-center gap-1"
                  data-testid={`admin-faq-delete-${item.id}`}
                >
                  <Trash size={12} weight="bold" /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
